export type EstimatedImpressionChannel = {
  id?: string | number;
  fk_channel_id?: string | number;
  channel_id?: string | number;
  name?: string | null;
  label?: string | null;
  channel_name?: string | null;
};

export type EstimatedImpressionBenchmark = {
  channelId: string | number;
  channelName: string;
  campaignGoalId?: number | null;
  campaignGoal?: string | null;
  metric: string;
  costLow: number;
  costHigh: number;
};

export type ImpressionEstimation = {
  enabled: boolean;
  status: string;
  campaignGoalId: number | null;
  channelIds: (string | number)[];
  canParticipantSelectSpecificChannels?: boolean;
  benchmarks: EstimatedImpressionBenchmark[];
};

export type EstimatedImpressionsResult = {
  isAvailable: boolean;
  lowImpressions: number | null;
  highImpressions: number | null;
  displayText: string | null;
  excludedChannels: string[];
  unavailableReason?: "missing-campaign-goal" | "no-matching-goal";
};

type GetEstimatedImpressionsParams = {
  budget?: string | number | null;
  brandContribution?: string | number | null;
  channels?: EstimatedImpressionChannel[];
  restrictToChannels?: boolean;
  allocateBudgetAcrossBenchmarkedChannels?: boolean;
  impressionEstimation?: ImpressionEstimation | null;
};

const unavailableEstimate: EstimatedImpressionsResult = {
  isAvailable: false,
  lowImpressions: null,
  highImpressions: null,
  displayText: null,
  excludedChannels: [],
};

export function getImpressionEstimation(
  campaign?: Record<string, unknown> | null
) {
  return campaign?.impressionEstimation as
    | ImpressionEstimation
    | null
    | undefined;
}

export function canShowImpressionEstimation(
  campaign?: Record<string, unknown> | null
): boolean {
  const configuration = getImpressionEstimation(campaign);
  return (
    configuration?.enabled === true && configuration.campaignGoalId != null
  );
}

function toNumber(value?: string | number | null): number {
  const parsed =
    typeof value === "string" ? Number(value.replace(/[$,\s]/g, "")) : value;
  return typeof parsed === "number" && Number.isFinite(parsed) ? parsed : 0;
}

function roundEstimatedImpressionBound(
  value: number,
  direction: "down" | "up"
): number {
  const increment = value < 1000 ? 1 : 1000;
  return (
    (direction === "down"
      ? Math.floor(value / increment)
      : Math.ceil(value / increment)) * increment
  );
}

export function formatEstimatedImpressionsRange(
  lowImpressions: number,
  highImpressions: number
): string {
  const formatter = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  });
  return `Est. Impressions: ${formatter.format(roundEstimatedImpressionBound(lowImpressions, "down"))} - ${formatter.format(roundEstimatedImpressionBound(highImpressions, "up"))}`;
}

export function getEstimatedImpressions({
  budget,
  brandContribution,
  channels = [],
  restrictToChannels = false,
  allocateBudgetAcrossBenchmarkedChannels = false,
  impressionEstimation,
}: GetEstimatedImpressionsParams): EstimatedImpressionsResult {
  const totalBudget = toNumber(budget) + toNumber(brandContribution);
  if (impressionEstimation?.enabled !== true || totalBudget <= 0)
    return unavailableEstimate;
  if (impressionEstimation.campaignGoalId == null) {
    return {
      ...unavailableEstimate,
      unavailableReason: "missing-campaign-goal",
    };
  }

  const configuredIds = new Set(impressionEstimation.channelIds.map(String));
  let channelIds =
    restrictToChannels ||
    impressionEstimation.canParticipantSelectSpecificChannels
      ? new Set(
          channels
            .filter(
              (channel) =>
                channel.id != null && configuredIds.has(String(channel.id))
            )
            .map((channel) => String(channel.id))
        )
      : configuredIds;
  if (!channelIds.size) return unavailableEstimate;

  if (allocateBudgetAcrossBenchmarkedChannels) {
    channelIds = new Set(
      Array.from(channelIds).filter((channelId) => {
        const benchmark = impressionEstimation.benchmarks.find(
          (entry) =>
            String(entry.channelId) === channelId &&
            entry.campaignGoalId === impressionEstimation.campaignGoalId
        );
        return (
          benchmark?.metric === "CPM" &&
          Number.isFinite(benchmark.costLow) &&
          Number.isFinite(benchmark.costHigh) &&
          benchmark.costLow > 0 &&
          benchmark.costHigh >= benchmark.costLow
        );
      })
    );
    if (!channelIds.size) return unavailableEstimate;
  }

  const channelBudget = totalBudget / channelIds.size;
  let low = 0;
  let high = 0;
  let includedChannels = 0;
  let matchingGoalChannels = 0;
  const excludedChannels: string[] = [];

  for (const channelId of channelIds) {
    const benchmark = impressionEstimation.benchmarks.find(
      (entry) =>
        String(entry.channelId) === channelId &&
        entry.campaignGoalId === impressionEstimation.campaignGoalId
    );
    if (benchmark) matchingGoalChannels += 1;
    if (
      !benchmark ||
      benchmark.metric !== "CPM" ||
      !Number.isFinite(benchmark.costLow) ||
      !Number.isFinite(benchmark.costHigh) ||
      benchmark.costLow <= 0 ||
      benchmark.costHigh < benchmark.costLow
    ) {
      excludedChannels.push(benchmark?.channelName || channelId);
      continue;
    }
    low += (channelBudget / benchmark.costHigh) * 1000;
    high += (channelBudget / benchmark.costLow) * 1000;
    includedChannels += 1;
  }

  if (!matchingGoalChannels) {
    return {
      ...unavailableEstimate,
      excludedChannels,
      unavailableReason: "no-matching-goal",
    };
  }
  if (!includedChannels || !Number.isFinite(low) || !Number.isFinite(high)) {
    return { ...unavailableEstimate, excludedChannels };
  }
  const lowImpressions = roundEstimatedImpressionBound(low, "down");
  const highImpressions = roundEstimatedImpressionBound(high, "up");
  return {
    isAvailable: true,
    lowImpressions,
    highImpressions,
    displayText: formatEstimatedImpressionsRange(
      lowImpressions,
      highImpressions
    ),
    excludedChannels,
  };
}
